import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiCheck, FiAlertCircle } from 'react-icons/fi';
import { stripeConnectService } from '../services/stripeConnectService';

const DealerStripeCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');

  const code = searchParams.get('code');
  const state = searchParams.get('state'); // dealer_id
  const dealerId = searchParams.get('dealer_id') || state;

  useEffect(() => {
    if (code && dealerId) {
      completeConnection();
    } else if (dealerId) {
      // Returning from account link flow — check status
      checkStatus();
    } else {
      setError('Missing parameters');
      setStatus('error');
    }
  }, []);

  const completeConnection = async () => {
    const result = await stripeConnectService.completeStripeConnect(code, dealerId);
    if (result.error) {
      setError(result.error.message || 'Failed to connect Stripe');
      setStatus('error');
    } else {
      setStatus('success');
    }
  };

  const checkStatus = async () => {
    const result = await stripeConnectService.checkStripeStatus(dealerId);
    if (result.connected) {
      setStatus('success');
    } else {
      setStatus('pending');
    }
  };

  return (
    <>
      <Helmet>
        <title>Stripe Connection - ShedX</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-xl p-8 text-center">
          {status === 'processing' && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#635bff] mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Connecting Stripe...</h2>
              <p className="text-gray-500">Finalizing your payment connection.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiCheck className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Stripe Connected!</h2>
              <p className="text-gray-500 mb-6">
                Your Stripe account is linked. Payments from shed sales will go directly to your account.
              </p>
              <button
                onClick={() => navigate('/admin')}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white font-medium py-3 rounded-lg transition-colors"
              >
                Go to Dashboard
              </button>
            </>
          )}

          {status === 'pending' && (
            <>
              <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiAlertCircle className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Connection Pending</h2>
              <p className="text-gray-500 mb-6">
                Stripe setup isn't complete yet. You may need to finish verifying your account.
              </p>
              <button
                onClick={() => navigate('/admin')}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white font-medium py-3 rounded-lg transition-colors"
              >
                Go to Dashboard
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiAlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Connection Failed</h2>
              <p className="text-gray-500 mb-6">{error}</p>
              <button
                onClick={() => navigate('/admin')}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white font-medium py-3 rounded-lg transition-colors"
              >
                Return to Dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DealerStripeCallback;
